import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { gql } from 'graphql-request';
import { useAuthContext } from '../../../context/AuthContextProvider';

// Placeholder: the identifier an MDR entry will carry in the getConnectedProducts `product`
// field. The real value is not known yet — swap this constant when it is.
export const MDR_PRODUCT_IDENTIFIER = 'MDR_PLACEHOLDER';

const GET_CONNECTED_PRODUCTS = gql`
  query getConnectedProducts($accountId: String!) {
    getConnectedProducts(accountId: $accountId) {
      products {
        instanceId
        product
        connected
      }
    }
  }
`;

interface ConnectedProductsResponse {
  products: { instanceId: string; product: string; connected: boolean }[];
}

/**
 * Whether the current account has an MDR product connected.
 *
 * Returns false until MDR is surfaced in getConnectedProducts; callers are expected to OR
 * this with their own feature flag rather than rely on it alone.
 */
const useMdrSubscription = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { accountId } = useAuthContext();

  const { data, isLoading } = useGqlQuery<ConnectedProductsResponse>({
    // Distinct from the Settings page's connected-products key: that query selects more fields,
    // and sharing a key would let whichever resolves first serve a partial payload to the other.
    queryKey: [QueryKey.ConnectedProducts, 'mdrSubscription', accountId],
    query: GET_CONNECTED_PRODUCTS,
    variables: { accountId },
    enabled: enabled && !!accountId,
  });

  const hasMdrSubscription = (data?.products ?? []).some(({ product, connected }) => product === MDR_PRODUCT_IDENTIFIER && connected);

  return { hasMdrSubscription, isLoading };
};

export default useMdrSubscription;
